import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import { useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import useSettingsParams from 'hooks/useSettingsParams';
import * as apiWarehouse from 'api/apiWarehouses';
import * as apiProductSet from 'api/apiProductSets';
import * as apiProduct from 'api/apiProducts';
import type { LeadCloudFileType, ProductType, WarehouseComingRowType, WarehouseComingAiItemType } from '@econtractor/types';
import useComingAiInfo from './useComingAiInfo';
import { matchProductByName } from './matching';
import { useNeedPay } from 'modules/admin';

interface UseOffComingParams {
    show: boolean | number;
    setShow: (show: boolean | number) => void;
    fetchStocks: (force?: boolean) => Promise<unknown> | unknown;
}

const useOffComing = ({ show, setShow, fetchStocks }: UseOffComingParams) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const { showToast } = useToast();
    const { auth } = useAuth();
    const { localSettings } = useSettings();
    const params = useSettingsParams();
    const canCredit = Boolean(params.creditControl);
    const { needPay } = useNeedPay();
    const rate = Number(localSettings?.RATE?.current || 1);

    const [addProduct, setAddProduct] = useState<any>(null);
    const [addSet, setAddSet] = useState<any>(null);
    const [addSourceType, setAddSourceType] = useState<'product' | 'set' | 'ai'>('product');
    const [setCount, setSetCount] = useState<number | string>('');
    const [setDescription, setSetDescription] = useState('');
    const [setAddProcess, setSetAddProcess] = useState(false);
    const [aComing, setAComing] = useState<WarehouseComingRowType[]>([]);
    const [comingProcess, setComingProcess] = useState(false);
    const [aiUploadFile, setAiUploadFile] = useState<LeadCloudFileType | null>(null);
    const [aiPricesInUsd, setAiPricesInUsd] = useState(false);

    const buildAiDescription = (fileName: string, line: number, suffix = '') =>
        `${t('labelAiDocPrefix')}: ${fileName}, ${t('labelAiDocLine')}: ${line}${suffix}`;

    const normalizeAiPriceToUsd = (priceRaw: number | null): number => {
        const price = Number(priceRaw ?? 0);
        if (!Number.isFinite(price) || price <= 0) return 0;
        // Single-currency mode: amounts are already in local currency (rate === 1).
        if (rate === 1) return price;
        if (aiPricesInUsd) return price;
        if (!Number.isFinite(rate) || rate <= 0) return price;
        return Number((price / rate).toFixed(2));
    };

    const mapAiRowToComing = (
        parsed: WarehouseComingAiItemType,
        cloudFile: LeadCloudFileType,
        fileName: string,
        products: ProductType[],
    ): WarehouseComingRowType | null => {
        const resolvedName = String(parsed?.prodName || '').trim();
        if (!resolvedName) return null;
        const match = matchProductByName(resolvedName, products);
        const matched = match.product;
        const price = normalizeAiPriceToUsd(Number(parsed?.price ?? 0)) || Number(matched?.prodDefPrice ?? 0);
        const priceSale = Number(matched?.prodDefPriceSale ?? price);
        const descriptionSuffix = match.mode === 'new' ? ` (${t('labelAiNewProduct')})` : '';

        return {
            setId: 0,
            product: {
                ID: Number(matched?.ID || 0),
                value: Number(matched?.ID || 0),
                label: matched?.prodName || resolvedName,
                vendor: matched?.vendorName || '',
                article: matched?.prodArticle || String(parsed?.prodArticle || ''),
                defPrice: Number(matched?.prodDefPrice || price || 0),
                defPriceSale: Number(matched?.prodDefPriceSale || priceSale || 0),
            },
            coming: {
                cCount: Number(parsed?.count || 0),
                cDescription: buildAiDescription(fileName, Number(parsed?.line || 0), descriptionSuffix),
                price,
                priceRate: price * rate,
                priceSale,
                priceSaleRate: priceSale * rate,
                payment: canCredit,
                paymentDescription: canCredit
                    ? buildAiDescription(fileName, Number(parsed?.line || 0))
                    : '',
                paymentAttachmentsCloud: canCredit ? [cloudFile] : [],
                aiMeta: {
                    mode: match.mode,
                    sourceName: resolvedName,
                    score: match.score,
                    fileName,
                    cloudFile,
                    sourceLine: Number(parsed?.line || 0),
                },
            },
        };
    };

    const handleAiParsed = async (items: WarehouseComingAiItemType[], cloudFile: LeadCloudFileType, fileName: string) => {
        if (!items.length) return;
        const resProducts = await apiProduct.getList({
            order: 'order by prod_name',
            onPage: 99999999,
            status: [1],
        });
        const products = Array.isArray(resProducts?.products) ? resProducts.products : [];

        const rows = items
            .map((item) => mapAiRowToComing(item, cloudFile, fileName, products))
            .filter(Boolean) as WarehouseComingRowType[];
        if (!rows.length) return;
        setAComing((prev) => [...prev, ...rows]);
        setAiUploadFile(cloudFile);
    };

    const { refInputFile, isBusy: aiBusy, uploadProgress, processingLabel, phase: aiPhase, handleOpenClick, handleChangeFile, handleCancel } =
        useComingAiInfo({ onParsed: handleAiParsed });

    const ensureAiProductsCreated = async (rows: WarehouseComingRowType[]): Promise<WarehouseComingRowType[] | null> => {
        const nextRows = [...rows];
        for (let index = 0; index < nextRows.length; index += 1) {
            const row = nextRows[index];
            if (Number(row?.product?.ID || 0) > 0) continue;

            const generatedArticle = `AIWH-${Date.now()}-${index + 1}`;
            const createResult = await apiProduct.save(0, {
                ownerId: Number(auth?.user?.ID || 0),
                prodArticle: generatedArticle,
                prodName: row?.coming?.aiMeta?.sourceName || row?.product?.label || 'AI Product',
                prodDescription: '',
                prodImages: '',
                prodFiles: '',
                vendorId: 0,
                dimensionId: 0,
                prodDefPrice: Number(row?.coming?.price || 0),
                prodDefPriceSale: Number(row?.coming?.priceSale || row?.coming?.price || 0),
            });
            if (!createResult || !createResult.stat) {
                showToast(t('common:messError'), 'danger');
                return null;
            }

            const productId = Number(createResult.stat);
            const product = productId > 0 ? await apiProduct.getProduct(productId) : null;
            if (!product?.ID) {
                showToast(t('common:messError'), 'danger');
                return null;
            }

            nextRows[index] = {
                ...row,
                product: {
                    ID: Number(product.ID),
                    value: Number(product.ID),
                    label: product.prodName || row.product.label,
                    vendor: product.vendorName || row.product.vendor,
                    article: product.prodArticle || row.product.article,
                    defPrice: Number(product.prodDefPrice || row.product.defPrice || row.coming.price || 0),
                    defPriceSale: Number(product.prodDefPriceSale || row.product.defPriceSale || row.coming.priceSale || 0),
                },
            };
        }
        return nextRows;
    };

    const handleClickApproved = async () => {
        if (needPay()) return;
        let rowsForSave = [...aComing];
        if (rowsForSave.some((row) => Number(row?.product?.ID || 0) <= 0)) {
            const createdRows = await ensureAiProductsCreated(rowsForSave);
            if (!createdRows) return;
            rowsForSave = createdRows;
            setAComing(createdRows);
        }

        const comingData = rowsForSave.map((c) => {
            const payment = canCredit && Boolean(c.coming.payment);
            return {
                productId: c.product.ID,
                count: c.coming.cCount,
                description: c.coming.cDescription,
                price: c.coming.price,
                priceSale: c.coming.priceSale,
                priceRate: c.coming.priceRate,
                priceSaleRate: c.coming.priceSaleRate,
                payment,
                paymentAmount: payment ? Number(c.coming.cCount || 0) * c.coming.price : 0,
                paymentAmountRate: payment ? Number(c.coming.cCount || 0) * c.coming.priceRate : 0,
                paymentDescription: payment ? c.coming.paymentDescription : '',
                paymentAttachments: [],
                paymentAttachmentsCloud: payment ? (c.coming.paymentAttachmentsCloud || []) : [],
                setId: Number(c.setId || 0),
            };
        });

        setComingProcess(true);
        const resComing = await apiWarehouse.coming2MainWarehouse(comingData as any);
        await Promise.resolve(fetchStocks(true));
        if (resComing) {
            showToast(t('messComingOk'), 'success');
            setShow(false);
        } else showToast(t('common:messError'), 'danger');
        handleHide();
        setComingProcess(false);
    };

    const handleChangeProduct = (opt: any) => {
        setAddProduct(opt);
        setAddSet(null);
    };

    const handleChangeSet = (opt: any) => {
        setAddSet(opt);
        setAddProduct(null);
    };

    const handleSelectType = (type: 'product' | 'set' | 'ai') => {
        setAddSourceType(type);
        setAddProduct(null);
        setAddSet(null);
        setSetCount('');
        setSetDescription('');
    };

    const handleCancelSetComing = () => {
        setAddSet(null);
        setSetCount('');
        setSetDescription('');
    };

    const handleAddSetComing = async () => {
        if (setAddProcess) return;
        const qty = Number(setCount);
        if (!addSet?.value) return;
        if (!Number.isFinite(qty) || qty <= 0) {
            showToast(t('messInputCount'), 'danger');
            return;
        }

        setSetAddProcess(true);
        try {
            const [resSet, resProducts] = await Promise.all([
                apiProductSet.getProductSet(addSet.value),
                apiProduct.getList({
                    order: 'order by prod_name',
                    onPage: 99999999,
                    status: [1],
                }),
            ]);
            if (!resSet?.items?.length) {
                showToast(t('common:messError'), 'danger');
                return;
            }

            const productsMap = new Map((resProducts?.products || []).map((p: any) => [Number(p.ID), p]));
            const comingRows = resSet.items
                .map((item: any) => {
                    const productId = Number(item.productId || 0);
                    const currentProduct = productsMap.get(productId);
                    const itemCount = Number(item.itemCount || 0) * qty;

                    return {
                        setId: Number(resSet.ID || addSet.value || 0),
                        product: {
                            ID: productId,
                            value: productId,
                            label: currentProduct?.prodName || item.prodName || `#${productId}`,
                            vendor: currentProduct?.vendorName || item.vendorName || '',
                            article: currentProduct?.prodArticle || item.prodArticle || '',
                        },
                        coming: {
                            cCount: itemCount,
                            cDescription: setDescription || '',
                            price: Number(currentProduct?.prodDefPrice || 0),
                            priceRate: Number(currentProduct?.prodDefPrice || 0) * rate,
                            priceSale: Number(currentProduct?.prodDefPriceSale || 0),
                            priceSaleRate: Number(currentProduct?.prodDefPriceSale || 0) * rate,
                            payment: false,
                        },
                    };
                })
                .filter((row: any) => Number(row?.coming?.cCount || 0) > 0);

            if (!comingRows.length) {
                showToast(t('common:messError'), 'danger');
                return;
            }

            setAComing((prev) => [...prev, ...(comingRows as WarehouseComingRowType[])]);
            setAddSet(null);
            setSetCount('');
            setSetDescription('');
        } finally {
            setSetAddProcess(false);
        }
    };

    const handleHide = () => {
        setShow(false);
        setAddProduct(null);
        setAddSet(null);
        setSetCount('');
        setSetDescription('');
        setSetAddProcess(false);
        setAiUploadFile(null);
        setAiPricesInUsd(false);
        setTimeout(() => {
            setAComing([]);
        }, 300);
    };

    useEffect(() => {
        setAddProduct(null);
        setAddSet(null);
        setSetCount('');
        setSetDescription('');
        setSetAddProcess(false);
        setAiUploadFile(null);
        setAiPricesInUsd(false);
    }, [show]);

    useEffect(() => {
        if (addProduct) document.getElementById('inputCount')?.focus();
    }, [addProduct]);

    return {
        rate,
        canCredit,
        addProduct,
        addSet,
        addSourceType,
        setCount,
        setDescription,
        setAddProcess,
        aComing,
        comingProcess,
        aiPricesInUsd,
        aiBusy,
        uploadProgress,
        processingLabel,
        aiPhase,
        refInputFile,
        setSetCount,
        setSetDescription,
        setAiPricesInUsd,
        setAddProduct,
        setAComing,
        handleClickApproved,
        handleChangeProduct,
        handleChangeSet,
        handleSelectType,
        handleCancelSetComing,
        handleAddSetComing,
        handleHide,
        handleOpenClick,
        handleChangeFile,
        handleCancel,
    };
};

export default useOffComing;
